/**
 * Mock Data Service Layer
 *
 * This service provides a toggle between mock data (Figma-aligned) and real API data.
 * Controlled by REACT_APP_USE_MOCK_DATA environment variable.
 *
 * Development (.env.development): REACT_APP_USE_MOCK_DATA=true → Uses Figma mock data
 * Production (.env.production): REACT_APP_USE_MOCK_DATA=false → Uses real API
 */

import { ParsedAuction } from '../contexts';
import { fetchAuctions, fetchPastAuctions, fetchAuctionById } from './auctionService';
import {
  mockHeroAuction,
  mockActiveAuctions,
  mockSidebarProduct,
  convertToWinnersFormat,
  mockWinnersData,
} from '../lib/figmaMockData';

// Check environment variable for mock data toggle
const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === 'true';

/**
 * Get all active/current auctions
 * Mock: Returns curated active auctions for the auctions listing
 * API: Returns active auctions from API
 */
export const getAuctions = async (): Promise<ParsedAuction[]> => {
  if (USE_MOCK_DATA) {
    console.log('🎨 Using mock active auctions');
    return mockActiveAuctions;
  }

  console.log('🌐 Fetching auctions from API');
  return fetchAuctions();
};

/**
 * Get the active/featured auction
 * Mock: Returns "Bunda Hardcore" from Figma design (2.777 Kč)
 * API: Returns first auction from API
 */
export const getActiveAuction = async (): Promise<ParsedAuction | null> => {
  if (USE_MOCK_DATA) {
    console.log('🎨 Using mock hero auction (Bunda Hardcore)');
    return mockHeroAuction;
  }

  console.log('🌐 Fetching active auction from API');
  const auctions = await fetchAuctions();
  return auctions[0] || null;
};

/**
 * Get past/completed auctions
 * Mock: Returns 12 products for Winners page grid (Figma design)
 * API: Returns completed auctions from API
 */
export const getPastAuctions = async (): Promise<ParsedAuction[]> => {
  if (USE_MOCK_DATA) {
    console.log('🎨 Using mock winners data (12 products)');
    return convertToWinnersFormat(mockWinnersData);
  }

  console.log('🌐 Fetching past auctions from API');
  return fetchPastAuctions();
};

/**
 * Get auction by ID
 * Mock: Not implemented (falls back to API)
 * API: Fetches specific auction from API
 */
export const getAuctionById = async (auctionId: number): Promise<ParsedAuction> => {
  if (USE_MOCK_DATA) {
    const activeAuction = mockActiveAuctions.find((auction) => auction.auctionId === auctionId);
    if (activeAuction) {
      return activeAuction;
    }

    if (auctionId === mockSidebarProduct.auctionId) {
      return mockSidebarProduct;
    }

    const winners = convertToWinnersFormat(mockWinnersData);
    const winnerMatch = winners.find((auction) => auction.auctionId === auctionId);
    if (winnerMatch) {
      return winnerMatch;
    }
  }

  console.log(`🌐 Fetching auction ${auctionId} from API`);
  return fetchAuctionById(auctionId);
};

/**
 * Get sidebar product (for Blog, About Us, Contact, Help pages)
 * Mock: Returns consistent yacht product (100.000 Kč)
 * API: Returns first active auction
 */
export const getSidebarProduct = async (): Promise<ParsedAuction | null> => {
  if (USE_MOCK_DATA) {
    console.log('🎨 Using mock sidebar product (100.000 Kč yacht)');
    return mockSidebarProduct;
  }

  console.log('🌐 Fetching sidebar product from API');
  return getActiveAuction();
};

/**
 * Check if mock data is enabled
 * Useful for debugging and conditional logic
 */
export const isMockDataEnabled = (): boolean => {
  return USE_MOCK_DATA;
};
